import api from "@/shared/services/apiClient";
import i18n from "@/i18n";

export interface ExportFilters {
  country?: string[];
  sending_org?: string;
  company?: string[];
  batch_no?: string;
}

export interface ImportRow {
  row: number;
  user_id: string;
  name: string;
  action?: "created" | "updated";
  temp_login_id?: string;
  temp_password?: string;
}

export interface ImportResult {
  /** Upload log id — lets the results panel offer the results CSV download. */
  logId?: number;
  succeeded: ImportRow[];
  failed: (ImportRow & { reason: string })[];
}

export interface ReferenceCodes {
  sendingOrgs: string[];
  companyCodes: string[];
  visaTypes: string[];
}

export interface UploadLogEntry {
  id: number;
  file_name: string | null;
  uploaded_at: string;
  total_rows: number;
  success_count: number;
  error_count: number;
  status: "processing" | "completed" | "partial" | "failed";
  uploaded_by_name: string | null;
}

export interface UploadLogRow {
  row_number: number;
  user_id: string | null;
  full_name: string | null;
  status: "success" | "error";
  error_detail: string | null;
  created_at: string;
  action: "created" | "updated" | null;
  temp_login_id: string | null;
  temp_password: string | null;
}

const downloadBlob = (data: BlobPart, disposition: string | undefined, fallback: string) => {
  const match = (disposition ?? "").match(/filename="?([^";\r\n]+)"?/);
  const url = URL.createObjectURL(new Blob([data], { type: "text/csv;charset=utf-8;" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = match?.[1] ?? fallback;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export const bulkUserService = {
  /**
   * Downloads filtered user + visa data for the officer's BU as a CSV file.
   * Triggers a browser file download directly.
   */
  exportCsv: async (filters: ExportFilters = {}): Promise<void> => {
    const params = new URLSearchParams();
    filters.country?.forEach((c) => params.append("country", c));
    if (filters.sending_org) params.set("sending_org", filters.sending_org);
    filters.company?.forEach((c) => params.append("company", c));
    if (filters.batch_no)    params.set("batch_no",    filters.batch_no);
    params.set("lang", i18n.language ?? "en");

    const endpoint = `/bulk-users/export${params.toString() ? `?${params}` : ""}`;
    const { data, headers } = await api.get(endpoint, {
      responseType: "blob",
    });

    downloadBlob(data, headers["content-disposition"] as string | undefined, "users_export.csv");
  },

  downloadTemplate: async (mode: "new"): Promise<void> => {
    const { data, headers } = await api.get(`/bulk-users/template?mode=${mode}&lang=${encodeURIComponent(i18n.language ?? "en")}`, { responseType: "blob" });
    downloadBlob(data, headers["content-disposition"] as string | undefined, "new_users_template.csv");
  },

  exportUploadResults: async (id: number): Promise<void> => {
    const { data, headers } = await api.get(`/bulk-users/history/${id}/export?lang=${encodeURIComponent(i18n.language ?? "en")}`, { responseType: "blob" });
    downloadBlob(data, headers["content-disposition"] as string | undefined, `upload_${id}_results.csv`);
  },

  /**
   * Regenerates a temporary password for a still-pending worker. The backend
   * endpoint takes the password from the caller, so it is generated here
   * (8 hex chars, same shape as the import) and echoed back for display.
   */
  resetTempPassword: async (userId: string): Promise<{ password: string }> => {
    const bytes = crypto.getRandomValues(new Uint8Array(4));
    const newPassword = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
    const { data } = await api.patch(`/profile/reset-password/${userId}`, { newPassword });
    return { password: (data as { newPassword?: string }).newPassword ?? newPassword };
  },

  /**
   * Starts a background import job. Returns the logId immediately (HTTP 202).
   * Poll getUploadHistory() to track progress.
   */
  importCsv: async (file: File): Promise<{ logId: number }> => {
    const formData = new FormData();
    formData.append("file", file);
    const { data } = await api.post("/bulk-users/import", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data as { logId: number };
  },

  /** Fetches valid sending_org, company_code, and visa_type codes for the BU. */
  getReferenceCodes: async (): Promise<ReferenceCodes> => {
    const { data } = await api.get("/bulk-users/reference-codes");
    return data as ReferenceCodes;
  },

  /** Returns the 20 most-recent upload sessions for the officer's BU. */
  getUploadHistory: async (): Promise<UploadLogEntry[]> => {
    const { data } = await api.get("/bulk-users/history");
    return data as UploadLogEntry[];
  },

  /** Returns per-row detail for a specific upload session. */
  getUploadHistoryDetail: async (id: number): Promise<UploadLogRow[]> => {
    const { data } = await api.get(`/bulk-users/history/${id}`);
    return data as UploadLogRow[];
  },
};
